import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { cn } from '@trafiflow/shared-ui'
import {
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  AlertTriangle,
  SkipForward,
} from 'lucide-react'
import type { PipelineNodeData } from './types'
import type { StageStatus } from '@/services/ad-generator/types'
import { getStageDefinition, PHASE_COLORS } from './utils/stageDefinitions'

interface StatusConfig {
  icon: typeof Clock
  color: string
  bgColor: string
  borderColor: string
  animate?: boolean
}

const STATUS_CONFIG: Record<StageStatus, StatusConfig> = {
  pending: {
    icon: Clock,
    color: 'text-tf-text-muted',
    bgColor: 'bg-tf-bg-muted',
    borderColor: 'border-tf-border',
  },
  running: {
    icon: Loader2,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500',
    animate: true,
  },
  completed: {
    icon: CheckCircle,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/50',
  },
  degraded: {
    icon: AlertTriangle,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/50',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/50',
  },
  skipped: {
    icon: SkipForward,
    color: 'text-tf-text-muted',
    bgColor: 'bg-tf-bg-muted',
    borderColor: 'border-tf-border',
  },
}

interface PipelineNodeProps {
  data: PipelineNodeData
}

function PipelineNodeComponent({ data }: PipelineNodeProps) {
  const { stage, isActive, onClick } = data
  const config = STATUS_CONFIG[stage.status] || STATUS_CONFIG.pending
  const Icon = config.icon
  const definition = getStageDefinition(stage.id)
  const phaseColors = definition ? PHASE_COLORS[definition.phase] : null

  return (
    <>
      {/* Target handle (left side) */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-2 !h-2 !bg-tf-border !border-none"
      />

      <div
        onClick={onClick}
        className={cn(
          'w-40 px-3 py-2.5 rounded-lg border-2 transition-all duration-200',
          'cursor-pointer hover:scale-105 hover:shadow-lg',
          config.bgColor,
          config.borderColor,
          isActive && 'ring-2 ring-blue-500/40 ring-offset-2 ring-offset-tf-bg',
        )}
      >
        {/* Phase indicator */}
        {phaseColors && (
          <div
            className={cn(
              'absolute -top-1 -right-1 w-3 h-3 rounded-full',
              phaseColors.bg,
              'border',
              phaseColors.border,
            )}
          />
        )}

        {/* Header with icon and name */}
        <div className="flex items-center gap-2 mb-1">
          <Icon
            className={cn(
              'w-4 h-4 flex-shrink-0',
              config.color,
              config.animate && 'animate-spin',
            )}
          />
          <span className={cn('text-xs font-semibold truncate', config.color)}>
            {stage.displayName}
          </span>
        </div>

        {/* Insight or status */}
        <div className="text-[10px] text-tf-text-muted truncate">
          {stage.insight ? (
            stage.insight.length > 30
              ? `${stage.insight.slice(0, 30)}...`
              : stage.insight
          ) : (
            definition?.description || ''
          )}
        </div>

        {/* Duration badge */}
        {stage.durationMs && stage.status === 'completed' && (
          <div className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded text-[9px] bg-tf-bg-card border border-tf-border text-tf-text-muted">
            {(stage.durationMs / 1000).toFixed(1)}s
          </div>
        )}
      </div>

      {/* Source handle (right side) */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-2 !h-2 !bg-tf-border !border-none"
      />
    </>
  )
}

export default memo(PipelineNodeComponent)
